import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Chip,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    Grid,
    CircularProgress,
    Alert,
    Badge,
    Divider,
    useTheme,
    alpha,
} from '@mui/material';
import {
    Person as PersonIcon,
    Computer as ComputerIcon,
    Phone as PhoneIcon,
    Tablet as TabletIcon,
    AccessTime as AccessTimeIcon,
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    Circle as CircleIcon,
    SignalCellular4Bar as SignalIcon,
    SignalCellular0Bar as OfflineIcon,
    Close as CloseIcon,
    Favorite as HeartIcon,
    Visibility as EyeIcon,
    AdminPanelSettings as adminIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { toast } from 'react-hot-toast';

// Animated Heartbeat Component
const AnimatedHeartbeat = ({ isActive = true }) => {
    const [scale, setScale] = React.useState(1);

    React.useEffect(() => {
        if (!isActive) return;

        const interval = setInterval(() => {
            setScale(1.2);
            setTimeout(() => setScale(1), 200);
        }, 1000);

        return () => clearInterval(interval);
    }, [isActive]);

    return (
        <HeartIcon
            sx={{
                fontSize: 16,
                color: isActive ? '#ef4444' : '#9ca3af',
                transform: `scale(${scale})`,
                transition: 'transform 0.2s ease-in-out',
                animation: isActive ? 'heartbeat 1s ease-in-out infinite' : 'none',
                '@keyframes heartbeat': {
                    '0%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.2)' },
                    '100%': { transform: 'scale(1)' },
                },
            }}
        />
    );
};

const OnlineAdmins = () => {
    const [onlineAdmins, setOnlineAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [showAdminDetails, setShowAdminDetails] = useState(false);
    const theme = useTheme();
    const { user: currentUser } = useAuth();

    const fetchOnlineAdmins = async () => {
        try {
            setRefreshing(true);
            const response = await api.dashboard.getOnlineAdmins();
            const admins = Array.isArray(response) ? response : [];

            const transformedAdmins = admins.map(admin => ({
                _id: admin.userId,
                fullname: admin.fullname,
                username: admin.username,
                email: admin.email,
                type: admin.type,
                lastActivity: admin.lastActivity,
                lastHeartbeat: admin.lastHeartbeat,
                onlineStatus: admin.onlineStatus,
                lastActivityFormatted: admin.lastActivityFormatted,
                deviceCount: admin.deviceCount || 0,
                deviceInfo: admin.deviceInfo || [],
                status: admin.onlineStatus === 'online' ? 'active' : 'inactive'
            }));

            setOnlineAdmins(transformedAdmins);
            setError('');
        } catch (err) {
            setError(err.message || 'Error fetching online admins');
            toast.error('Failed to fetch online admins');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchOnlineAdmins();
        const interval = setInterval(fetchOnlineAdmins, 30000);
        return () => clearInterval(interval);
    }, []);

    const handleRefresh = () => {
        fetchOnlineAdmins();
    };

    const handleAdminClick = (admin) => {
        setSelectedAdmin(admin);
        setShowAdminDetails(true);
    };

    const getDeviceIcon = (deviceType) => {
        switch (deviceType) {
            case 'mobile':
                return <PhoneIcon fontSize="small" />;
            case 'tablet':
                return <TabletIcon fontSize="small" />;
            case 'desktop':
                return <ComputerIcon fontSize="small" />;
            default:
                return <ComputerIcon fontSize="small" />;
        }
    };

    const getTimeAgo = (timestamp) => {
        if (!timestamp) return 'Never';
        const now = new Date();
        const time = new Date(timestamp);
        const diffInSeconds = Math.floor((now - time) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        return `${Math.floor(diffInSeconds / 86400)}d ago`;
    };

    const getStatusColor = (status) => {
        return status === 'online' || status === 'active' ? '#22c55e' : '#ef4444';
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'superadmin':
                return theme.palette.error.main;
            case 'admin':
                return theme.palette.primary.main;
            case 'agent':
                return theme.palette.success.main;
            default:
                return theme.palette.grey[500];
        }
    };

    const getDeviceTypeColor = (deviceType) => {
        switch (deviceType) {
            case 'mobile':
                return theme.palette.primary.main;
            case 'tablet':
                return theme.palette.secondary.main;
            case 'desktop':
                return theme.palette.success.main;
            default:
                return theme.palette.grey[500];
        }
    };

    const getStatusIcon = (status) => {
        return status === 'online' || status === 'active'
            ? <SignalIcon sx={{ color: '#22c55e' }} />
            : <OfflineIcon sx={{ color: '#ef4444' }} />;
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                    Online Admins
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                        icon={<CircleIcon sx={{ color: '#22c55e' }} />}
                        label={`${onlineAdmins.length} Online`}
                        color="success"
                        variant="outlined"
                    />
                    <Tooltip title="Refresh">
                        <IconButton
                            onClick={handleRefresh}
                            disabled={refreshing}
                            sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                },
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                        <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Currently Online Admins ({onlineAdmins.length})
                            </Typography>
                        </Box>

                        {onlineAdmins.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <adminIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">
                                    No admins currently online
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Admins will appear here when they are actively using the platform
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{ p: 0 }}>
                                {onlineAdmins.map((admin, index) => (
                                    <React.Fragment key={admin._id || admin.id}>
                                        <ListItem
                                            sx={{
                                                p: 2,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                                },
                                            }}
                                            onClick={() => handleAdminClick(admin)}
                                        >
                                            <ListItemAvatar>
                                                <Badge
                                                    overlap="circular"
                                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                    badgeContent={getStatusIcon(admin.onlineStatus)}
                                                >
                                                    <Avatar sx={{ width: 48, height: 48, bgcolor: alpha(getTypeColor(admin.type), 0.2) }}>
                                                        {(admin.fullname || admin.username || admin.email || 'A').charAt(0).toUpperCase()}
                                                    </Avatar>
                                                </Badge>
                                            </ListItemAvatar>

                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                            {admin.fullname || admin.username || 'Unknown Admin'}
                                                        </Typography>
                                                        <Chip
                                                            label={admin.type?.toUpperCase() || 'ADMIN'}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: getTypeColor(admin.type),
                                                                color: 'white',
                                                                fontSize: '0.65rem',
                                                                height: 18,
                                                            }}
                                                        />
                                                        {admin.onlineStatus === 'online' && (
                                                            <Chip
                                                                label="Online"
                                                                size="small"
                                                                sx={{
                                                                    bgcolor: '#22c55e',
                                                                    color: 'white',
                                                                    fontSize: '0.65rem',
                                                                    height: 18,
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <AccessTimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                                            <Typography variant="body2" color="text.secondary">
                                                                Last seen: {admin.lastActivityFormatted || getTimeAgo(admin.lastActivity)}
                                                            </Typography>
                                                        </Box>
                                                        {admin.lastHeartbeat && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <AnimatedHeartbeat isActive={admin.onlineStatus === 'online'} />
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Last heartbeat: {getTimeAgo(admin.lastHeartbeat)}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                }
                                            />

                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {admin.deviceInfo?.map((device, deviceIndex) => (
                                                    <Tooltip
                                                        key={deviceIndex}
                                                        title={`${device.deviceType || 'Unknown'} - ${device.platform || 'Unknown platform'} - ${device.os || 'Unknown OS'}`}
                                                    >
                                                        <Box
                                                            sx={{
                                                                p: 0.5,
                                                                borderRadius: 1,
                                                                bgcolor: alpha(getDeviceTypeColor(device.deviceType), 0.1),
                                                                color: getDeviceTypeColor(device.deviceType),
                                                            }}
                                                        >
                                                            {getDeviceIcon(device.deviceType)}
                                                        </Box>
                                                    </Tooltip>
                                                ))}
                                                <Tooltip title="View Details">
                                                    <IconButton size="small">
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </ListItem>
                                        {index < onlineAdmins.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card elevation={2} sx={{ borderRadius: 3 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                                Admin Online Stats
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Superadmins Online</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {onlineAdmins.filter(a => a.type === 'superadmin').length}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Admins Online</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {onlineAdmins.filter(a => a.type === 'admin').length}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">Agents Online</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {onlineAdmins.filter(a => a.type === 'agent').length}
                                    </Typography>
                                </Box>
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Total Online</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                                        {onlineAdmins.length}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Modal shows when showAdminDetails is true */}
            {selectedAdmin && showAdminDetails && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1300,
                    }}
                    onClick={() => setShowAdminDetails(false)}
                >
                    <Card
                        sx={{
                            maxWidth: 500,
                            width: '90%',
                            maxHeight: '80vh',
                            overflow: 'auto',
                            position: 'relative',
                            borderRadius: 3,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <CardContent>
                            <IconButton
                                onClick={() => setShowAdminDetails(false)}
                                sx={{ position: 'absolute', top: 8, right: 8 }}
                            >
                                <CloseIcon />
                            </IconButton>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <Avatar sx={{ width: 64, height: 64, bgcolor: alpha(getTypeColor(selectedAdmin.type), 0.2) }}>
                                    {(selectedAdmin.fullname || selectedAdmin.username || selectedAdmin.email || 'A').charAt(0).toUpperCase()}
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {selectedAdmin.fullname || selectedAdmin.username || 'Unknown Admin'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedAdmin.email}
                                    </Typography>
                                    <Chip
                                        label={selectedAdmin.type?.toUpperCase() || 'ADMIN'}
                                        size="small"
                                        sx={{
                                            mt: 0.5,
                                            bgcolor: getTypeColor(selectedAdmin.type),
                                            color: 'white',
                                        }}
                                    />
                                </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Username
                                    </Typography>
                                    <Typography variant="body1">
                                        {selectedAdmin.username || 'Not provided'}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Online Status
                                    </Typography>
                                    <Chip
                                        label={selectedAdmin.onlineStatus || 'Unknown'}
                                        size="small"
                                        sx={{
                                            bgcolor: getStatusColor(selectedAdmin.onlineStatus),
                                            color: 'white',
                                        }}
                                    />
                                </Box>

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Last Seen
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <EyeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                        <Typography variant="body1">
                                            {selectedAdmin.lastActivityFormatted || getTimeAgo(selectedAdmin.lastActivity)}
                                        </Typography>
                                    </Box>
                                </Box>

                                {selectedAdmin.lastHeartbeat && (
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Last Heartbeat
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AnimatedHeartbeat isActive={selectedAdmin.onlineStatus === 'online'} />
                                            <Typography variant="body1">
                                                {getTimeAgo(selectedAdmin.lastHeartbeat)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}

                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Connected Devices ({selectedAdmin.deviceCount || 1})
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                                        {selectedAdmin.deviceInfo?.map((device, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    bgcolor: alpha(theme.palette.grey[100], 0.5),
                                                }}
                                            >
                                                {getDeviceIcon(device.deviceType)}
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {device.deviceType?.toUpperCase() || 'UNKNOWN'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {device.platform || 'Unknown platform'} • {device.os || 'Unknown OS'}
                                                    </Typography>
                                                    {device.browser && (
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            Browser: {device.browser}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            )}
        </Box>
    );
};

export default OnlineAdmins;
